// ABOUTME: Modal for searching and linking entities to a support ticket.
// ABOUTME: Uses global search (with includeWebshopCredentials flag) and links the picked entity.

import { Box, Button, useTheme } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorBoundary } from "react-error-boundary";

import { tokens } from "../../../theme";
import MSearch from "../../theme/Search/MSearch";
import Text from "../../theme/text/Text";
import ShTextSkeleton from "../../theme/skeleton/ShTextSkeleton";
import SearchEmpty from "../../theme/Search/SearchEmpty";
import SearchError from "../../theme/Search/SearchError";

import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import CheckIcon from "@mui/icons-material/Check";
import Subheading from "../../theme/text/Subheading";
import { ReactComponent as TableEmpty } from "../../../styles/svg/tableempty.svg";

import { useSearchQuery } from "../../../newapi/global/searchSlice";
import { useAddSupportTicketLinkedItemMutation } from "../../../newapi/warehouse/supportTicketApiSlice";
import { search_keys } from "../../../utils/searchKeys";
import { search_key_to_support_ticket_linked_type } from "../../../utils/staticEnums";
import {
    showToastMessagePostRequest,
    showToastMessageRequestError,
} from "../../../utils/toasts";

// Per-key icon override for keys not in searchKeys.js.
const KEY_ICON_OVERRIDE = {
    webshopcredentials: <StoreOutlinedIcon />,
};

const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
};

const MAddSupportTicketLinkedItem = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [value, setValue] = useState("");
    const [valueToSearchWith, setValueToSearchWith] = useState("");

    // currentData (not data) so stale results from a previous term are dropped
    // when the search is cleared or skipped, instead of lingering in the cache.
    const { currentData, isLoading, isFetching, isError } = useSearchQuery(
        { searchTerm: valueToSearchWith, includeWebshopCredentials: true, relationId: props.relationId },
        { skip: valueToSearchWith.length === 0 }
    );

    const [addLinkedItem] = useAddSupportTicketLinkedItemMutation();

    const debouncedSet = useCallback(
        debounce((v) => setValueToSearchWith(v), 500),
        []
    );

    const handleSearchChange = (event) => {
        const v = event.target.value;
        setValue(v);
        debouncedSet(v);
    };

    // Discard the current search whenever the modal closes so it reopens fresh.
    const handleClose = () => {
        setValue("");
        setValueToSearchWith("");
        props.handleClose();
    };

    const groupedData = (currentData ?? []).reduce((acc, item) => {
        if (!search_key_to_support_ticket_linked_type[item.key]) return acc;
        if (!acc[item.key]) acc[item.key] = [];
        acc[item.key].push(item);
        return acc;
    }, {});

    const groupedKeys = Object.keys(groupedData);

    const linkedSet = useMemo(() => {
        const set = new Set();
        for (const l of props.linkedItems ?? []) {
            set.add(`${l.linkedType}:${l.linkedId}`);
        }
        return set;
    }, [props.linkedItems]);

    const handleSelect = async (linkedType, linkedId, displayReference, displayName) => {
        const payload = {
            linkedType,
            linkedId,
            displayReference: displayReference != null ? String(displayReference) : null,
            displayName: displayName != null ? String(displayName) : null,
        };

        // Local mode: caller manages its own state, no API call.
        if (props.onSelect) {
            props.onSelect(payload);
            handleClose();
            return;
        }

        if (!props.ticketId) return;
        try {
            const result = await addLinkedItem({
                id: props.ticketId,
                data: payload,
            }).unwrap();
            showToastMessagePostRequest(t, result);
            handleClose();
        } catch (err) {
            showToastMessageRequestError(t("create_failed"), t, err);
        }
    };

    const showEmpty =
        !isLoading &&
        !isFetching &&
        valueToSearchWith.length > 0 &&
        groupedKeys.length === 0;

    return (
        <MSearch
            open={props.open}
            handleClose={handleClose}
            value={value}
            handleSearchChange={handleSearchChange}
            title={props.title ?? t("link_item")}
            modalSx={{ zIndex: 1400 }}
            sx={{
                body : {
                    minWidth:"unset",
                    width:600,
                    "@media screen and (max-width: 47.9375em)" : {
                        width:1,
                    }
                }
            }}
            searchContainer={
                <Box sx={{ width: 1, overflow: "auto",}}>
                    {valueToSearchWith.length === 0 && (
                        <Box
                            sx={{
                                py: 3,
                                pb:5,
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <Box
                                sx={{
                                    maxWidth: 300,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                }}
                            >
                                <TableEmpty />
                                <Box
                                    sx={{
                                        mt: 3,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 0.5,
                                    }}
                                >
                                    <Subheading textAlign="center">
                                        {t("search_to_start_title")}
                                    </Subheading>
                                    <Text align="center" light>
                                        {t("search_to_start_description")}
                                    </Text>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {(isLoading || isFetching) && (
                        <Box
                            sx={{
                                pt: 3,
                                pb: 3,
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                mx: 3,
                            }}
                        >
                            <ShTextSkeleton sx={{ height: 40, borderRadius: 3 }} />
                            <ShTextSkeleton sx={{ height: 40, borderRadius: 3 }} />
                            <ShTextSkeleton sx={{ height: 40, borderRadius: 3 }} />
                        </Box>
                    )}

                    {showEmpty && (
                        <Box sx={{ pt: 3, pb: 6 }}>
                            <SearchEmpty />
                        </Box>
                    )}

                    {isError && (
                        <Box sx={{ pt: 3, pb: 6 }}>
                            <SearchError />
                        </Box>
                    )}

                    {!isLoading && !isFetching && groupedKeys.map((key, sIdx) => {
                        const items = groupedData[key];
                        const meta = search_keys[key];
                        const linkedType = search_key_to_support_ticket_linked_type[key];
                        const icon = meta?.icon ?? KEY_ICON_OVERRIDE[key] ?? null;
                        return (
                            <Box
                                key={"klist" + sIdx}
                                sx={{
                                    py: 3,
                                    borderBottom: `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                                }}
                            >
                                <Box sx={{ mb: 1, px: 3 }}>
                                    <Text light color={colors.txt["secondary"]}>
                                        {t(key)}
                                    </Text>
                                </Box>
                                <Box px={1.5}>
                                    {items.map((item, idx) => {
                                        const stateColor =
                                            meta?.getStateColor?.(item.state, theme) || "grey";
                                        const useGreyFallback =
                                            theme.palette.mode === "dark" && stateColor === "primaryGrey";
                                        const swatchBg = useGreyFallback
                                            ? colors.grey[500]
                                            : colors[stateColor]?.[100] ?? colors.grey[100];
                                        const swatchFg = useGreyFallback
                                            ? colors.txt["secondary"]
                                            : colors[stateColor]?.[400] ?? colors.txt["primary"];
                                        const isLinked = linkedSet.has(`${linkedType}:${item.id}`);
                                        return (
                                            <ErrorBoundary
                                                key={`item-${sIdx}-${idx}`}
                                                FallbackComponent={() => (
                                                    <div>{t("something_went_wrong")}</div>
                                                )}
                                                onError={(error, info) => console.error(error, info)}
                                            >
                                                <Button
                                                    // disabled={isLinked}
                                                    onClick={() =>{
                                                        if(isLinked) return;
                                                        handleSelect(
                                                            linkedType,
                                                            item.id,
                                                            item.displayReference ?? item.title,
                                                            item.displayName ?? item.subtitle
                                                        )
                                                    }}
                                                    color={stateColor ?? "grey"}
                                                    sx={{
                                                        width: 1,
                                                        px: 1.5,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 2,
                                                        py: 1,
                                                        borderRadius: 4,
                                                        textTransform: "unset",
                                                        fontWeight: "unset",
                                                        justifyContent: "flex-start",
                                                        opacity:  1,
                                                        "&.Mui-disabled": {
                                                            opacity: 0.55,
                                                            color: colors.txt["secondary"],
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: 3,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            background: swatchBg,
                                                            color: swatchFg,
                                                            border: `1px solid ${swatchFg}`,
                                                            flexShrink: 0,
                                                            "& svg path": {
                                                                fill: swatchFg,
                                                            },
                                                        }}
                                                    >
                                                        {icon}
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            textAlign: "left",
                                                            minWidth: 0,
                                                            flex: 1,
                                                        }}
                                                    >
                                                        <Text semibold>
                                                            {meta?.titleAddon ? t(meta.titleAddon) + " " : ""}
                                                            {item.title}
                                                        </Text>
                                                        <Text light>
                                                            {meta?.formatSubTitle
                                                                ? meta.formatSubTitle(item.subtitle)
                                                                : item.subtitle}
                                                        </Text>
                                                    </Box>
                                                    {isLinked && (
                                                        <Box
                                                            sx={{
                                                                ml: "auto",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 0.5,
                                                                height: 24,
                                                                px: 1,
                                                                borderRadius: 3,
                                                                background:
                                                                    theme.palette.mode === "dark"
                                                                        ? colors.grey[400]
                                                                        : colors.grey[100],
                                                                color: colors.txt["secondary"],
                                                                fontFamily: "Roboto, sans-serif",
                                                                fontSize: 12,
                                                                fontWeight: 500,
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            <CheckIcon sx={{ fontSize: 14 }} />
                                                            {t("linked")}
                                                        </Box>
                                                    )}
                                                </Button>
                                            </ErrorBoundary>
                                        );
                                    })}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            }
        />
    );
};

export default MAddSupportTicketLinkedItem;
